const React = require('react');
const T = require('prop-types');
const { useState, useEffect, useRef, useCallback } = React;
const { context } = require('app-context');
const { STUDENT_OFFER_ROLE_IDS, USER_ROLE_IDS } = require('utils/constants');
const useForm = require('hooks/useForm');
const FormConfig = require('./formConfig');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: AddIcon } = require('@mui/icons-material/Add');
const { default: DeleteIcon } = require('@mui/icons-material/Delete');

const selectors = context.selectors.all;
const actions = context.actions;
const redux = context.redux.hooks;

const internals = {};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}-${Date.now()}`;

const emptyRow = () => ({
    key: nextRowKey(),
    targetId: null,
    schoolId: null,
    roleIds: [],
    allStudentsSelected: false
});

const allTargetRoleIds = (target) => (String(target.targetValue).split(':')[1] || '')
    .split(',')
    .filter(Boolean)
    .map(Number);

const isAllSchoolRoleTarget = (target) => (
    target.targetType === 'schoolRole' && String(target.targetValue).split(':')[0] === 'all'
);

const isAllProspectsTarget = (target) => {

    const roleIds = allTargetRoleIds(target);

    return roleIds.length > 0 && roleIds.every((id) => USER_ROLE_IDS.PROSPECT.includes(id));
};

const getAllSchoolRoleTargets = (targets) => (targets || []).filter(isAllSchoolRoleTarget);

const targetsAllStudents = (targets) => getAllSchoolRoleTargets(targets).some((target) => !isAllProspectsTarget(target));

const targetsAllProspects = (targets) => getAllSchoolRoleTargets(targets).some(isAllProspectsTarget);

const getBroadTargetingKind = (targets) => {

    const allStudents = targetsAllStudents(targets);
    const allProspects = targetsAllProspects(targets);

    if (allStudents && allProspects) {
        return 'both';
    }

    if (allStudents) {
        return 'students';
    }

    if (allProspects) {
        return 'prospects';
    }

    return null;
};

const BROAD_TARGETING_CONFIRM = {
    students: {
        title: 'Target all students across all schools?',
        content: 'This offer will be shown to every student at every school. Are you sure you want to continue?'
    },
    prospects: {
        title: 'Target all prospects across all schools?',
        content: 'This offer will be shown to every prospect at every school. Are you sure you want to continue?'
    },
    both: {
        title: 'Target all students and prospects across all schools?',
        content: 'This offer will be shown to every student and every prospect at every school. Are you sure you want to continue?'
    }
};

const mapCustomRows = (schoolRoleTargets) => schoolRoleTargets.map((t) => {

    const [schoolPart, rolePart] = String(t.targetValue).split(':');

    return {
        key: nextRowKey(),
        targetId: t.id,
        schoolId: Number(schoolPart),
        roleIds: (rolePart || '').split(',').filter(Boolean).map(Number),
        allStudentsSelected: false
    };
});

const deriveInitialModel = (initialTargets) => {

    const targets = initialTargets || [];
    const schoolRoleTargets = targets.filter((t) => t.targetType === 'schoolRole');
    const allTargets = schoolRoleTargets.filter(isAllSchoolRoleTarget);
    const customTargets = schoolRoleTargets.filter((target) => !isAllSchoolRoleTarget(target));
    const allProspectsTarget = allTargets.find(isAllProspectsTarget);
    const allStudentsTarget = allTargets.find((target) => !isAllProspectsTarget(target));
    const rows = customTargets.length > 0 ? mapCustomRows(customTargets) : [emptyRow()];

    return {
        allStudents: !!allStudentsTarget,
        allProspects: !!allProspectsTarget,
        rows,
        allStudentsTargetId: allStudentsTarget?.id ?? null,
        allProspectsTargetId: allProspectsTarget?.id ?? null
    };
};

const buildSchoolRoleTarget = ({ id, targetValue }) => {

    const target = {
        targetType: 'schoolRole',
        targetValue,
        combinationType: 'OR'
    };

    // Only targets loaded from the API carry an ID. Omitting it tells the API this
    // is a new row; persisted IDs let the API update without deleting/recreating.
    if (Number.isInteger(id)) {
        target.id = id;
    }

    return target;
};

const buildTargets = ({
    allStudents,
    allProspects,
    rows,
    allStudentsTargetId,
    allProspectsTargetId
}) => {

    const targets = [];

    if (allStudents) {
        targets.push(buildSchoolRoleTarget({
            id: allStudentsTargetId,
            targetValue: `all:${STUDENT_OFFER_ROLE_IDS.join(',')}`
        }));
    }

    if (allProspects) {
        targets.push(buildSchoolRoleTarget({
            id: allProspectsTargetId,
            targetValue: `all:${USER_ROLE_IDS.PROSPECT.join(',')}`
        }));
    }

    if (allStudents && allProspects) {
        return targets;
    }

    return targets.concat(rows
        .filter((row) => row.schoolId && row.roleIds.length > 0)
        .map((row) => buildSchoolRoleTarget({
            id: row.targetId,
            targetValue: `${row.schoolId}:${row.roleIds.join(',')}`
        })));
};

const isEmptyRow = (row) => !row.schoolId && (!row.roleIds || row.roleIds.length === 0);

const isCompleteRow = (row) => row.schoolId && row.roleIds.length > 0;

const computeIsValid = (allStudents, allProspects, rows) => {

    if (allStudents && allProspects) {
        return true;
    }

    if (allStudents || allProspects) {
        return rows.every((row) => isEmptyRow(row) || isCompleteRow(row));
    }

    if (rows.length === 0) {
        return false;
    }

    return rows.every(isCompleteRow);
};

const getCustomRoleAudience = (allStudents, allProspects) => {

    if (allStudents && !allProspects) {
        return 'prospects';
    }

    if (allProspects && !allStudents) {
        return 'students';
    }

    return 'both';
};

const filterAvailableRoles = (roles, audience) => {

    if (audience === 'prospects') {
        return roles.filter((role) => FormConfig.isProspectOfferRole(role));
    }

    if (audience === 'students') {
        return roles.filter((role) => !FormConfig.isProspectOfferRole(role));
    }

    return roles;
};

const CUSTOM_ROW_COPY = {
    both: {
        caption: 'Add a row per school and pick which student or prospect roles can see this offer.',
        error: 'Each row needs a school and at least one role.',
        allRolesLabel: 'All Students',
        emptyRolesLabel: 'Select roles',
        rolesFieldLabel: 'Roles'
    },
    students: {
        caption: 'Add a row per school to also target student roles at specific schools.',
        error: 'Each extra school row needs a school and at least one student role.',
        allRolesLabel: 'All Students',
        emptyRolesLabel: 'Select student roles',
        rolesFieldLabel: 'Student roles'
    },
    prospects: {
        caption: 'Add a row per school to also target prospect roles at specific schools.',
        error: 'Each extra school row needs a school and at least one prospect role.',
        allRolesLabel: 'All Prospects',
        emptyRolesLabel: 'Select prospect roles',
        rolesFieldLabel: 'Prospect roles'
    }
};

module.exports = function OfferTargeting(props) {

    const { initialTargets, onChange, onValidityChange } = props;
    const { TargetingRow } = internals;

    const initialModelRef = useRef(null);
    if (initialModelRef.current === null) {
        initialModelRef.current = deriveInitialModel(initialTargets);
    }

    const [allStudents, setAllStudents] = useState(initialModelRef.current.allStudents);
    const [allProspects, setAllProspects] = useState(initialModelRef.current.allProspects);
    const [rows, setRows] = useState(initialModelRef.current.rows);
    const [schools, setSchools] = useState(() => selectors.getSchools(redux.getState()) || []);
    const [schoolsLoading, setSchoolsLoading] = useState(false);
    const [rolesBySchool, setRolesBySchool] = useState({});
    const [loadingSchoolIds, setLoadingSchoolIds] = useState([]);
    const showCustom = !(allStudents && allProspects);
    const customAudience = getCustomRoleAudience(allStudents, allProspects);
    const customCopy = CUSTOM_ROW_COPY[customAudience];

    const onChangeRef = useRef(onChange);
    useEffect(() => {

        onChangeRef.current = onChange;
    }, [onChange]);

    const onValidityChangeRef = useRef(onValidityChange);
    useEffect(() => {

        onValidityChangeRef.current = onValidityChange;
    }, [onValidityChange]);

    useEffect(() => {

        onChangeRef.current(buildTargets({
            allStudents,
            allProspects,
            rows,
            allStudentsTargetId: initialModelRef.current.allStudentsTargetId,
            allProspectsTargetId: initialModelRef.current.allProspectsTargetId
        }));

        if (onValidityChangeRef.current) {
            onValidityChangeRef.current(computeIsValid(allStudents, allProspects, rows));
        }
    }, [allStudents, allProspects, rows]);

    const rolesBySchoolRef = useRef(rolesBySchool);
    useEffect(() => {

        rolesBySchoolRef.current = rolesBySchool;
    }, [rolesBySchool]);

    const loadRolesForSchool = useCallback((schoolId) => {

        if (!schoolId || rolesBySchoolRef.current[schoolId]) {
            return;
        }

        setLoadingSchoolIds((prev) => prev.includes(schoolId) ? prev : [...prev, schoolId]);

        redux.dispatch(actions.dataFetching.fetchAppEntities({ entities: ['schoolDetails'], schoolId }))
            .then(() => {

                const persistedRoleIds = initialModelRef.current.rows
                    .filter((row) => String(row.schoolId) === String(schoolId))
                    .flatMap((row) => row.roleIds);
                const studentRoles = FormConfig.getStudentOfferRoles(
                    selectors.getSchoolRoles_byId(redux.getState(), schoolId) || [],
                    persistedRoleIds
                );

                setRolesBySchool((prev) => ({ ...prev, [schoolId]: studentRoles }));
            })
            .finally(() => setLoadingSchoolIds((prev) => prev.filter((id) => id !== schoolId)));
    }, []);

    useEffect(() => {

        if (schools.length === 0) {
            setSchoolsLoading(true);
            redux.dispatch(actions.dataFetching.fetchAppEntities({ entities: ['schools'] }))
                .then(() => setSchools(selectors.getSchools(redux.getState()) || []))
                .finally(() => setSchoolsLoading(false));
        }

        initialModelRef.current.rows.forEach((row) => loadRolesForSchool(row.schoolId));
    }, [loadRolesForSchool]);

    const restoreCustomRows = useCallback(() => {

        setRows((prev) => prev.length > 0 ? prev : [emptyRow()]);
    }, []);

    const handleToggleAllStudents = useCallback((event) => {

        const checked = event.target.checked;
        setAllStudents(checked);

        if (!(checked && allProspects)) {
            restoreCustomRows();
        }
    }, [allProspects, restoreCustomRows]);

    const handleToggleAllProspects = useCallback((event) => {

        const checked = event.target.checked;
        setAllProspects(checked);

        if (!(checked && allStudents)) {
            restoreCustomRows();
        }
    }, [allStudents, restoreCustomRows]);

    const handleAddRow = useCallback(() => {

        setRows((prev) => [...prev, emptyRow()]);
    }, []);

    const handleRemoveRow = useCallback((key) => {

        setRows((prev) => prev.filter((row) => row.key !== key));
    }, []);

    const handleSchoolChange = useCallback((key, school) => {

        const schoolId = school ? school.id : null;
        setRows((prev) => prev.map((row) => row.key === key ? {
            ...row,
            schoolId,
            roleIds: [],
            allStudentsSelected: false
        } : row));

        if (schoolId) {
            loadRolesForSchool(schoolId);
        }
    }, [loadRolesForSchool]);

    const handleRolesChange = useCallback((key, schoolId, value) => {

        const available = filterAvailableRoles(
            rolesBySchoolRef.current[schoolId] || [],
            customAudience
        );
        const allRoleIds = customAudience === 'both'
            ? available.filter((role) => !FormConfig.isProspectOfferRole(role)).map((role) => role.id)
            : available.map((role) => role.id);

        setRows((prev) => prev.map((row) => {

            if (row.key !== key) {
                return row;
            }

            return {
                ...row,
                ...FormConfig.resolveStudentRoleSelection({
                    allRoleIds,
                    previousAllStudentsSelected: row.allStudentsSelected,
                    value
                })
            };
        }));
    }, [customAudience]);

    return (
        <Box>
            <h3 style={{ marginBottom: 0 }}>Targeting Rules</h3>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                Choose who can see this offer. Offers are always hidden for schools that have offers disabled.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <FormControlLabel
                    control={<Checkbox checked={allStudents} onChange={handleToggleAllStudents} />}
                    label='Target all students across all schools'
                />
                <FormControlLabel
                    control={<Checkbox checked={allProspects} onChange={handleToggleAllProspects} />}
                    label='Target all prospects across all schools'
                />
            </Box>

            {showCustom && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
                        {customCopy.caption}
                    </Typography>

                    {rows.map((row) => {

                        const available = filterAvailableRoles(rolesBySchool[row.schoolId] || [], customAudience);
                        const isLoading = loadingSchoolIds.includes(row.schoolId);

                        return (
                            <TargetingRow
                                key={row.key}
                                allRolesLabel={customCopy.allRolesLabel}
                                availableRoles={available}
                                allStudentsSelected={row.allStudentsSelected}
                                emptyRolesLabel={customCopy.emptyRolesLabel}
                                isLoading={isLoading}
                                onRemove={() => handleRemoveRow(row.key)}
                                onRolesChange={(value) => handleRolesChange(row.key, row.schoolId, value)}
                                onSchoolChange={(schoolId) => handleSchoolChange(
                                    row.key,
                                    schools.find((school) => String(school.id) === String(schoolId)) || null
                                )}
                                roleIds={row.roleIds}
                                rolesFieldLabel={customCopy.rolesFieldLabel}
                                rowKey={row.key}
                                schoolId={row.schoolId}
                                schools={schools}
                                schoolsLoading={schoolsLoading}
                            />
                        );
                    })}

                    {!computeIsValid(allStudents, allProspects, rows) && (
                        <Typography variant='caption' color='error' sx={{ mt: 1, display: 'block' }}>
                            {customCopy.error}
                        </Typography>
                    )}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddRow}
                        size='small'
                        sx={{ mt: rows.length > 0 ? 1 : 0 }}
                    >
                        Add School
                    </Button>
                </Box>
            )}
        </Box>
    );
};

internals.TargetingRow = function TargetingRow(props) {

    const {
        onRemove,
        ...formConfigProps
    } = props;
    const { Form } = useForm(FormConfig.buildTargetingRowFormConfig(formConfigProps));

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {Form}
            </Box>
            <IconButton size='small' onClick={onRemove} color='error' aria-label='Remove row'>
                <DeleteIcon fontSize='small' />
            </IconButton>
        </Box>
    );
};

module.exports.targetsAllStudents = targetsAllStudents;
module.exports.targetsAllProspects = targetsAllProspects;
module.exports.getBroadTargetingKind = getBroadTargetingKind;
module.exports.BROAD_TARGETING_CONFIRM = BROAD_TARGETING_CONFIRM;
module.exports.deriveInitialModel = deriveInitialModel;
module.exports.buildTargets = buildTargets;
module.exports.computeIsValid = computeIsValid;
module.exports.getCustomRoleAudience = getCustomRoleAudience;
module.exports.filterAvailableRoles = filterAvailableRoles;

module.exports.propTypes = {
    initialTargets: T.array,
    onChange: T.func.isRequired,
    onValidityChange: T.func
};
